import { useQuery } from '@tanstack/react-query'
import { clientInstance } from 'utils/http-client'

export interface DoorSearchResult {
  item_id: string
  name: string
  domain: string
  latitude: number
  longitude: number
  address: string
  city: string
  state: string
  country: string
  zipcode: string
  chain_id: string
  chain_name: string
  chain_size: number
  type: string
  score: number
}

export interface ChainSearchResult {
  item_id: string
  name: string
  domain: string
  count: number
  type: string
  score: number
  city_summary?: string
  state_summary?: string
}

export interface CombinedSearchResult {
  doors: DoorSearchResult[]
  chains: ChainSearchResult[]
  search_type: string
}

export interface DoorsAndChainsSearchParams {
  name?: string
  domain?: string
}

function isStructuredParams(
  arg: string | DoorsAndChainsSearchParams
): arg is DoorsAndChainsSearchParams {
  return typeof arg === 'object' && arg !== null
}

export function useDoorsAndChainsSearch(
  arg: string | DoorsAndChainsSearchParams
) {
  const params = isStructuredParams(arg)
    ? { name: arg.name?.trim() ?? '', domain: arg.domain?.trim() ?? '' }
    : { name: '', domain: '', q: arg.trim() }

  const qs = new URLSearchParams()
  if ('q' in params && params.q) qs.set('q', params.q)
  if (params.name) qs.set('name', params.name)
  if (params.domain) qs.set('domain', params.domain)

  const hasInput =
    ('q' in params && !!params.q) || !!params.name || !!params.domain

  return useQuery<CombinedSearchResult>({
    queryKey: [
      'doors-and-chains-search',
      params.name,
      params.domain,
      'q' in params ? params.q : '',
    ],
    queryFn: async () => {
      const url = `common/search/doors_and_chains/?${qs.toString()}`
      const client = clientInstance
      const response = await client.get(url)
      return response.data
    },
    enabled: hasInput,
  })
}

export function useAllChainsSearch(query: string) {
  return useQuery<ChainSearchResult[]>({
    queryKey: ['all-chains-search', query],
    queryFn: async () => {
      const url = `common/search/chains/?q=${encodeURIComponent(query)}`
      const client = clientInstance
      const response = await client.get(url)
      return response.data
    },
  })
}
